import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { sendFeedback } from '@3dera/core/services/feedback';
import { getMaintenanceSettings } from '@3dera/core/services/admin';
import { stripHtml } from '@3dera/core/utils/validation';
import styles from './FeedbackModal.module.css';

const FEEDBACK_RATE_LIMIT_MS = 60000; // 1 gönderim / 60 saniye

const FeedbackModal = ({ onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  const { currentUser } = useAuth();
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const lastSubmitTime = useRef(0);
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = [
    { id: 'bug', label: 'Bug Bildirimi', emoji: '🐞' },
    { id: 'hata', label: 'Hata Bildirimi', emoji: '⚠️' },
    { id: 'geliştirme', label: 'Geliştirme Önerisi', emoji: '💡' },
    { id: 'geri bildirim', label: 'Geri Bildirim', emoji: '💬' }
  ];

  const categoryLabels = {
    'bug': 'Bug Bildirimi',
    'hata': 'Hata Bildirimi',
    'geliştirme': 'Geliştirme Önerisi',
    'geri bildirim': 'Geri Bildirim'
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleNext = () => {
    if (selectedCategory) {
      setStep(2);
      setError('');
    }
  };

  const handleBack = () => {
    setStep(1);
    setError('');
  };

  const MESSAGE_MAX_LENGTH = 5000;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const now = Date.now();
    if (now - lastSubmitTime.current < FEEDBACK_RATE_LIMIT_MS) {
      const waitSec = Math.ceil((FEEDBACK_RATE_LIMIT_MS - (now - lastSubmitTime.current)) / 1000);
      setError(`Çok sık gönderim. Lütfen ${waitSec} saniye bekleyin.`);
      return;
    }

    try {
      const maintenance = await getMaintenanceSettings();
      if (maintenance.success && maintenance.data?.enabled) {
        setError('Site şu an bakımda. Geri bildirim gönderilemiyor.');
        return;
      }
    } catch (err) {
      setError('Bakım durumu kontrol edilemedi. Lütfen tekrar deneyin.');
      return;
    }

    const raw = typeof message === 'string' ? message.trim() : '';
    if (!raw) {
      setError('Lütfen mesajınızı girin.');
      return;
    }
    const trimmed = stripHtml(raw).trim().slice(0, MESSAGE_MAX_LENGTH);
    if (!trimmed) {
      setError('Lütfen geçerli bir mesaj girin.');
      return;
    }
    if (raw.length > MESSAGE_MAX_LENGTH) {
      setError(`Mesaj en fazla ${MESSAGE_MAX_LENGTH} karakter olabilir.`);
      return;
    }

    setLoading(true);
    setError('');

    const ALLOWED_CATEGORIES = ['bug', 'hata', 'geliştirme', 'geri bildirim'];
    const categoryValid = typeof selectedCategory === 'string' && ALLOWED_CATEGORIES.includes(selectedCategory);

    if (!categoryValid) {
      setLoading(false);
      setError('Geçersiz kategori. Lütfen tekrar deneyin.');
      return;
    }

    const userEmail = currentUser?.email || null;
    const userId = currentUser?.uid || null;
    try {
      await sendFeedback(selectedCategory, trimmed, userEmail, userId);
      lastSubmitTime.current = Date.now();
      setSuccess(true);

      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      // console.error('Geri bildirim gönderme hatası:', err);
      setError(err.message || 'Geri bildirim gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={handleClose} aria-label="Kapat">
          ×
        </button>

        {success ? (
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>✅</div>
            <h2>Teşekkürler!</h2>
            <p>Geri bildiriminiz başarıyla gönderildi.</p>
          </div>
        ) : step === 1 ? (
          <div className={styles.stepContainer}>
            <h2 className={styles.modalTitle}>Gelişmemize yardımcı olun:</h2>
            <div className={styles.categoriesGrid}>
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`${styles.categoryCard} ${
                    selectedCategory === category.id ? styles.categoryCardSelected : ''
                  }`}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  <span className={styles.categoryEmoji}>{category.emoji}</span>
                  <span className={styles.categoryLabel}>{category.label}</span>
                  <div className={styles.radioButton}>
                    {selectedCategory === category.id && (
                      <div className={styles.radioButtonSelected} />
                    )}
                  </div>
                </button>
              ))}
            </div>
            <button
              className={`${styles.nextButton} ${
                !selectedCategory ? styles.nextButtonDisabled : ''
              }`}
              onClick={handleNext}
              disabled={!selectedCategory}
            >
              Sonraki
            </button>
          </div>
        ) : (
          <div className={styles.stepContainer}>
            <h2 className={styles.modalTitle}>
              {selectedCategory ? categoryLabels[selectedCategory] : 'Geri Bildirim'}
            </h2>
            <p className={styles.modalSubtitle}>Cevabınızın sebebi nedir?</p>
            <form onSubmit={handleSubmit} className={styles.feedbackForm}>
              <textarea
                className={styles.messageTextarea}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Geri bildiriminizi buraya yazın..."
                rows="8"
                required
                disabled={loading}
              />
              {error && <div className={styles.errorMessage}>{error}</div>}
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={handleBack}
                  disabled={loading}
                >
                  Geri
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading || !message.trim()}
                >
                  {loading ? 'Gönderiliyor...' : 'Gönder'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
